import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Plus, Briefcase, Search, Users, UserCheck, CheckCircle, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { setShowAddProjectModal } from '../../store/slices/modalsSlice';

const WelcomeSection = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [statsData, setStatsData] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);
  const [allPlanLimits, setAllPlanLimits] = useState(null);
  const [showPlanTable, setShowPlanTable] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/welcomeFourBox`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();

        if (data.success) {
          const { usage_stats } = data;

          const mapStat = (stat, title, icon) => ({
            title,
            value: stat.current,
            limit: stat.limit,
            icon,
            subtitle: stat.display,
            progress: stat.percentage,
            limitReached: stat.limit_reached,
            remaining: stat.remaining,
          });

          const formattedStats = [
            mapStat(usage_stats.total_brand, 'Projects', Briefcase),
            mapStat(usage_stats.total_competitor, 'Competitor Search', Search),
            mapStat(usage_stats.total_affiliate_gap, 'Affiliate Gap', Users),
            mapStat(usage_stats.total_contact_finder, 'Contact Finder', UserCheck),
          ];

          setStatsData(formattedStats);
          if (data.plan_details) setPlanInfo(data.plan_details);
          if (data.all_plan_limits) setAllPlanLimits(data.all_plan_limits);
        } else {
          throw new Error(data.message || 'Failed to fetch data');
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        setStatsData([
          { title: 'Projects', value: 0, limit: 1, icon: Briefcase, subtitle: '0/1', progress: 0, limitReached: false, remaining: 1 },
          { title: 'Competitor Search', value: 0, limit: 1, icon: Search, subtitle: '0/1', progress: 0, limitReached: false, remaining: 1 },
          { title: 'Affiliate Gap', value: 0, limit: 1, icon: Users, subtitle: '0/1', progress: 0, limitReached: false, remaining: 1 },
          { title: 'Contact Finder', value: 0, limit: 10, icon: UserCheck, subtitle: '0/10', progress: 0, limitReached: false, remaining: 10 },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleAddProject = () => {
    dispatch(setShowAddProjectModal(true));
  };

  const firstName = user?.firstName || user?.name?.split(' ')[0] || 'User';
  const currentPlan = planInfo?.current_plan?.toLowerCase() || user?.plan?.toLowerCase() || 'free';

  return (
    <div className="mb-6">
      {/* Welcome Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Welcome back, {firstName}
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            Here's an overview of your account usage
            {planInfo && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                {planInfo.current_plan} Plan
              </span>
            )}
          </p>
        </div>
        <button
          onClick={handleAddProject}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 transition-all duration-200 hover:shadow-md text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Add Project
        </button>
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-5 animate-pulse">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-slate-700 rounded-lg"></div>
                <div className="flex-1">
                  <div className="w-20 h-3 bg-gray-200 dark:bg-slate-700 rounded mb-2"></div>
                  <div className="w-10 h-5 bg-gray-200 dark:bg-slate-700 rounded"></div>
                </div>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statsData.map((stat, index) => {
            const cappedProgress = Math.min(stat.progress || 0, 100);
            const isLimitReached = stat.limitReached;

            return (
              <div
                key={index}
                className={`rounded-xl p-5 transition-all duration-200 hover:shadow-md relative overflow-hidden border ${
                  isLimitReached
                    ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                    : 'bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-700'
                }`}
              >
                {isLimitReached && (
                  <div className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg">
                    LIMIT REACHED
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isLimitReached
                      ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400'
                      : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  }`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-500 dark:text-slate-400 truncate">{stat.title}</p>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xl font-bold text-gray-900 dark:text-white">{stat.value}</span>
                      <span className="text-xs text-gray-400 dark:text-slate-500">
                        / {stat.limit === 'unlimited' ? <span className="italic">unlimited</span> : stat.limit}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`h-1.5 rounded-full overflow-hidden mb-2 ${
                  isLimitReached ? 'bg-red-100 dark:bg-red-900/30' : 'bg-gray-100 dark:bg-slate-700'
                }`}>
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ease-out ${
                      isLimitReached ? 'bg-red-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${cappedProgress}%` }}
                  ></div>
                </div>

                <div className="flex items-center justify-between">
                  {stat.limit === 'unlimited' ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                      <CheckCircle className="w-3 h-3" />
                      Unlimited
                    </span>
                  ) : isLimitReached ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                      <AlertTriangle className="w-3 h-3" />
                      Monthly limit reached
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                      <CheckCircle className="w-3 h-3" />
                      {stat.remaining ?? 0} remaining
                    </span>
                  )}
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                    isLimitReached
                      ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                      : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  }`}>
                    {stat.limit === 'unlimited' ? 'Active' : `${cappedProgress}%`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Plan Limits Comparison */}
      {allPlanLimits && (
        <div className="mt-4">
          <button
            onClick={() => setShowPlanTable(!showPlanTable)}
            className="flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          >
            {showPlanTable ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showPlanTable ? 'Hide' : 'View'} plan limits
          </button>

          {showPlanTable && (
            <div className="mt-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Feature</th>
                    {['Growth', 'Pro', 'Enterprise'].map((plan) => (
                      <th key={plan} className="text-center px-4 py-3">
                        <span className={`text-xs font-semibold uppercase tracking-wider ${
                          currentPlan === plan.toLowerCase()
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-500 dark:text-slate-400'
                        }`}>
                          {plan}
                        </span>
                        {currentPlan === plan.toLowerCase() && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                            CURRENT
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                  {[
                    { label: 'Projects', key: 'brand' },
                    { label: 'Competitor Search', key: 'competitor' },
                    { label: 'Affiliate Gap', key: 'gap' },
                    { label: 'Contact Finder', key: 'contact_finder' },
                  ].map((feature) => (
                    <tr key={feature.key} className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="px-4 py-3 text-gray-700 dark:text-slate-300 font-medium">{feature.label}</td>
                      {['growth', 'pro', 'enterprise'].map((plan) => {
                        const val = allPlanLimits[plan]?.[feature.key];
                        const isCurrent = currentPlan === plan;
                        return (
                          <td key={plan} className={`text-center px-4 py-3 ${isCurrent ? 'font-semibold text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-slate-400'}`}>
                            {val === 'unlimited' ? (
                              <span className="text-green-600 dark:text-green-400 font-medium">Unlimited</span>
                            ) : val}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WelcomeSection;
